import { CurrencyDollarSimple, Quotes } from "@phosphor-icons/react";
import * as React from "react";
import { Dropdown } from "../Dropdown";
import styles from "../Navbar.module.css";
import chromeIcon from "~/assets/Chrome-icon.svg";
import { ReadMoreLink } from "~/components/DesignSystem/ReadMoreLink";
import { useCursorFollow } from "~/components/DesignSystem/utils/useCursorFollow";

export interface ResourcesDropdownContentProps {
  isSwitching?: boolean;
  isExiting?: boolean;
  chromeExtensionUrl?: string;
}

// ROI icon - currency dollar for money/ROI
const RoiIcon = () => (
  <div className={`${styles.productIcon} ${styles.roiIcon}`}>
    <CurrencyDollarSimple size={19} weight="fill" />
  </div>
);

// Blog icon - quotes for content/testimonials
const BlogIcon = () => (
  <div className={`${styles.productIcon} ${styles.blogIcon}`}>
    <Quotes size={19} weight="fill" />
  </div>
);

const ChromeExtensionCard: React.FC<{ chromeExtensionUrl?: string }> = ({
  chromeExtensionUrl,
}) => {
  const cardRef = React.useRef<HTMLAnchorElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const { handleMouseMove, handleMouseEnter, handleMouseLeave } =
    useCursorFollow({
      elementRef: cardRef,
      onHoverClass: styles.chromeCardHovering,
    });

  const onMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    handleMouseEnter(e);
  };

  const onMouseLeave = () => {
    setIsHovered(false);
    handleMouseLeave();
  };

  const href = chromeExtensionUrl || "/chrome-extension";

  return (
    <a
      ref={cardRef}
      href={href}
      target={chromeExtensionUrl ? "_blank" : undefined}
      rel={chromeExtensionUrl ? "noopener noreferrer" : undefined}
      className={styles.chromeCard}
      onMouseMove={handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.chromeCardOverlay} aria-hidden="true" />
      <div className={styles.chromeCardContent}>
        <div className={styles.chromeCardText}>
          <span className={styles.chromeCardLabel}>Browser Extension</span>
          <span className={styles.chromeCardTitle}>
            Pricing intel,
            <br />
            everywhere you go
          </span>
        </div>
        <div className={styles.chromeCardVisual}>
          <img src={chromeIcon} alt="" className={styles.chromeCardIcon} />
        </div>
      </div>
      <div className={styles.chromeCardFooter}>
        <ReadMoreLink href={href} forceHover={isHovered} as="span">
          Add to Chrome
        </ReadMoreLink>
      </div>
    </a>
  );
};

export const ResourcesDropdownContent: React.FC<
  ResourcesDropdownContentProps
> = ({ isSwitching, isExiting, chromeExtensionUrl }) => (
  <Dropdown
    width="wide"
    featuredOnRight
    isSwitching={isSwitching}
    isExiting={isExiting}
    sections={[
      {
        title: "RESOURCES",
        layout: "row",
        items: [
          {
            icon: <RoiIcon />,
            title: "ROI",
            description:
              "See how Vendr delivers measurable savings and ROI for companies of all sizes",
            href: "/resources/roi",
          },
          {
            icon: <BlogIcon />,
            title: "Blog",
            description:
              "Read the latest insights, tips, and best practices for procurement and negotiations",
            href: "/blog",
          },
        ],
      },
    ]}
    featured={<ChromeExtensionCard chromeExtensionUrl={chromeExtensionUrl} />}
  />
);
